import { defineStore } from "pinia";
import type { CatalogPayload } from "@/api/catalog";
import { fetchCatalogList } from "@/api/catalog";
import {
  fetchDomainProbe,
  fetchDomainProbeProgress,
  runDomainProbe,
  type DomainProbeBatch,
  type DomainProbeItem,
  type DomainProbeResult
} from "@/api/probe";
import {
  fetchDomainHeat,
  heatLabel,
  type DomainHeatItem,
  type DomainHeatResult
} from "@/api/heat";

export type FavoriteViewMode = "folder" | "tag";
export type ProbeFilter = "all" | "ok" | "fail";

export const useFavoriteStore = defineStore("favorite", {
  state: () => ({
    editMode: false,
    viewMode: "folder" as FavoriteViewMode,
    selectedTag: "",
    tagVersion: 0,
    breadcrumb: [] as Array<string | number>,
    catalog: { tree: [], list: {} } as CatalogPayload,
    catalogId: 0,
    catalogVersion: 0,
    urlsVersion: 0,
    probeFilter: "all" as ProbeFilter,
    probeLoading: false,
    probeBatch: null as DomainProbeBatch | null,
    probeSummary: null as Omit<DomainProbeResult, "items" | "batch"> | null,
    probeByDomain: {} as Record<string, DomainProbeItem>,
    probeById: {} as Record<number, DomainProbeItem>,
    probePollTimer: null as ReturnType<typeof setInterval> | null,
    heatSummary: null as Omit<DomainHeatResult, "items" | "index_meta"> | null,
    heatByDomain: {} as Record<string, DomainHeatItem>,
    heatById: {} as Record<number, DomainHeatItem>,
    heatIndexMeta: null as DomainHeatResult["index_meta"]
  }),
  getters: {
    probeRunning(state): boolean {
      return state.probeBatch?.status === "running";
    }
  },
  actions: {
    setEditMode(value: boolean) {
      this.editMode = value;
    },
    setViewMode(mode: FavoriteViewMode) {
      this.viewMode = mode;
      this.bumpUrls();
    },
    setSelectedTag(tag: string) {
      this.selectedTag = tag;
      this.bumpUrls();
    },
    setProbeFilter(filter: ProbeFilter) {
      this.probeFilter = filter;
    },
    bumpTags() {
      this.tagVersion += 1;
    },
    setBreadcrumb(path: Array<string | number>) {
      this.breadcrumb = path;
    },
    setCatalog(catalog: CatalogPayload) {
      this.catalog = catalog;
    },
    setCatalogId(id: number) {
      this.catalogId = id;
    },
    bumpUrls() {
      this.urlsVersion += 1;
    },
    applyProbeItems(items: DomainProbeItem[]) {
      const byDomain: Record<string, DomainProbeItem> = { ...this.probeByDomain };
      const byId: Record<number, DomainProbeItem> = { ...this.probeById };
      for (const item of items) {
        byId[item.url_base_id] = item;
        if (item.url_base) {
          byDomain[item.url_base.toLowerCase()] = item;
        }
      }
      this.probeByDomain = byDomain;
      this.probeById = byId;
    },
    applyProbeResult(result: DomainProbeResult) {
      this.probeSummary = {
        probe_date: result.probe_date,
        total: result.total,
        probed: result.probed,
        ok: result.ok,
        fail: result.fail
      };
      this.applyProbeItems(result.items || []);
      if (result.batch) {
        this.applyProbeBatch(result.batch);
      }
    },
    applyProbeBatch(batch: DomainProbeBatch) {
      const rawItems = Object.values(batch.items || {});
      let ok = 0;
      let fail = 0;
      for (const item of rawItems) {
        if (item.state === "pending") {
          continue;
        }
        if (item.is_ok === true) {
          ok += 1;
        } else if (item.is_ok === false) {
          fail += 1;
        }
      }
      const normalized: DomainProbeBatch =
        rawItems.length > 0 ? { ...batch, ok, fail } : batch;
      this.probeBatch = normalized;
      const items = rawItems.map((item) => ({
        url_base_id: item.url_base_id,
        url_base: item.url_base,
        status_code: item.status_code ?? null,
        is_ok: item.is_ok ?? null,
        probed_at: item.probed_at ?? null,
        probed_today:
          item.probed_today || item.state === "done" || item.state === "skipped",
        probe_state: item.probe_state || item.state || "",
        state: item.state || "",
        error: item.error || ""
      }));
      this.applyProbeItems(items);
      this.probeSummary = {
        probe_date: normalized.probe_date || this.probeSummary?.probe_date || "",
        total: normalized.total,
        probed: ok + fail,
        ok: normalized.ok,
        fail: normalized.fail
      };
      if (normalized.status === "running" && normalized.batch_id) {
        this.startProbePolling(normalized.batch_id);
      } else {
        this.stopProbePolling();
      }
    },
    async loadProbeStatus() {
      const response = await fetchDomainProbe();
      this.applyProbeResult(response.data);
      return response.data;
    },
    async probeAllDomains() {
      this.probeLoading = true;
      try {
        const response = await runDomainProbe();
        this.applyProbeBatch(response.data);
        return response.data;
      } finally {
        this.probeLoading = false;
      }
    },
    startProbePolling(batchId: string) {
      this.stopProbePolling();
      this.probePollTimer = setInterval(() => {
        void this.pollProbeProgress(batchId);
      }, 1500);
    },
    stopProbePolling() {
      if (this.probePollTimer) {
        clearInterval(this.probePollTimer);
        this.probePollTimer = null;
      }
    },
    async pollProbeProgress(batchId: string) {
      try {
        const response = await fetchDomainProbeProgress(batchId);
        const batch = response.data;
        if (!batch || batch.status === "idle" || !batch.batch_id) {
          this.stopProbePolling();
          return;
        }
        this.applyProbeBatch(batch);
      } catch {
        this.stopProbePolling();
      }
    },
    probeOfDomain(domain: string | undefined | null): DomainProbeItem | null {
      if (!domain) return null;
      return this.probeByDomain[domain.toLowerCase()] || null;
    },
    applyHeatItems(items: DomainHeatItem[]) {
      const byDomain: Record<string, DomainHeatItem> = {};
      const byId: Record<number, DomainHeatItem> = {};
      for (const item of items) {
        byId[item.url_base_id] = item;
        if (item.url_base) {
          byDomain[item.url_base.toLowerCase()] = item;
        }
      }
      this.heatByDomain = byDomain;
      this.heatById = byId;
    },
    applyHeatResult(result: DomainHeatResult) {
      this.heatSummary = {
        total: result.total,
        ranked: result.ranked,
        unranked: result.unranked,
        pending: result.pending
      };
      this.heatIndexMeta = result.index_meta ?? null;
      this.applyHeatItems(result.items || []);
    },
    async loadHeatStatus() {
      const response = await fetchDomainHeat();
      this.applyHeatResult(response.data);
      return response.data;
    },
    heatOfDomain(domain: string | undefined | null): DomainHeatItem | null {
      if (!domain) return null;
      return this.heatByDomain[domain.toLowerCase()] || null;
    },
    heatLabelOfDomain(domain: string | undefined | null): string {
      return heatLabel(this.heatOfDomain(domain));
    },
    async reloadCatalog() {
      const response = await fetchCatalogList();
      const payload = response.data;
      this.setCatalog(payload);
      if (payload.tree.length > 0) {
        const firstId = payload.tree[0].id;
        this.setBreadcrumb([firstId]);
        this.setCatalogId(firstId);
      } else {
        this.setBreadcrumb([]);
        this.setCatalogId(0);
      }
      this.catalogVersion += 1;
      this.bumpTags();
      this.bumpUrls();
      return payload;
    },
    catalogNodeName(id: string | number): string {
      const node = this.catalog.list?.[String(id)];
      return node?.name ?? "";
    }
  }
});
